/**
 * Labs listing themselves from the public site (free lab listing, medopd-docs 90): SMS-verified sign-up,
 * the owner's setup checklist and verification documents, and the super admin's review queue.
 * Mirrors medopd-backend's lablisting package.
 */
import { fetchApi, fetchGetFileApi, fetchMultipartApi } from '../client';
import { captureStaffSession } from '../sessionToken';
import type { OpeningHours } from './publicProfile';
import type { CodeSent, SessionResult, SignupVerified } from './doctorListing';

export type LabListingStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED' | 'REVOKED';
export type LabDocumentKind = 'REGISTRATION' | 'OWNER_IDENTITY' | 'NABL';

export interface LabSignupCommand {
  grant: string;
  mobile: string;
  ownerName: string;
  email: string;
  password: string;
  labName: string;
  address: string;
  locality?: string;
  city: string;
  state?: string;
  pincode: string;
  phone: string;
  registrationAuthority: string;
  registrationNumber: string;
  nablNumber?: string;
  hours: OpeningHours[];
  termsAccepted: boolean;
  listingConsent: boolean;
  /** From api.emailVerification.verifyCode (purpose LAB_SIGNUP) for this same email. */
  emailGrant: string;
}

export interface LabDocumentView { kind: LabDocumentKind; contentType: string; sizeBytes: number; uploadedAt: string }

export interface LabSetupStatus {
  labName: string;
  verificationStatus: LabListingStatus;
  /** Why the review was rejected or revoked (shown to the lab). */
  reviewNote?: string | null;
  submittedAt?: string | null;
  documents: LabDocumentView[];
  /** The lab needs the NABL certificate too (an accreditation number was given). */
  nablClaimed: boolean;
  pagePublished: boolean;
  homeCollectionOn: boolean;
  labVisitsOn: boolean;
  testsListed: number;
  /** Visible on the public site now. */
  isPublic: boolean;
  /** Patients can book (public, at least one test, and home collection or lab visits on). */
  bookable: boolean;
  publicPath?: string | null;
  /** The registration as entered at sign-up. */
  registrationAuthority?: string;
  registrationNumber?: string;
  nablNumber?: string | null;
  /** The owner may correct the registration now (before review, or after a rejection). */
  canEditRegistration?: boolean;
}

export interface LabRegistrationUpdate { registrationAuthority: string; registrationNumber: string; nablNumber?: string }

export interface LabQueueItem {
  clinicId: string;
  labName: string;
  city?: string | null;
  pincode?: string | null;
  ownerName: string;
  registrationAuthority: string;
  registrationNumber: string;
  nablNumber?: string | null;
  status: LabListingStatus;
  submittedAt?: string | null;
  createdAt?: string | null;
  documents: LabDocumentView[];
  /** The last decision's note (why it was sent back or revoked) and when. */
  reviewNote?: string | null;
  reviewedAt?: string | null;
}

export interface LabQueuePage { items: LabQueueItem[]; page: number; size: number; total: number }

const post = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body), skipBodyLog: true });
const admin = (clinicId: string) => `/api/admin/lab-verifications/${encodeURIComponent(clinicId)}`;

export const labListingModule = {
  signup: {
    requestCode: (mobile: string) => fetchApi<CodeSent>('/api/public/lab-signup/otp/request', post({ mobile })),
    verifyCode: (challengeId: string, mobile: string, code: string) =>
      fetchApi<SignupVerified>('/api/public/lab-signup/otp/verify', post({ challengeId, mobile, code })),
    /** Creates the lab and its owner's account and starts a session (same response as a password login). */
    submit: async (cmd: LabSignupCommand) => {
      const response = await fetchApi<SessionResult>('/api/public/lab-signup', post(cmd));
      captureStaffSession(response);
      return response;
    },
  },

  setup: {
    get: () => fetchApi<LabSetupStatus>('/api/lab-listing/setup'),
    upload: (kind: LabDocumentKind, file: File) => {
      const form = new FormData();
      form.append('file', file);
      return fetchMultipartApi<LabDocumentView[]>(`/api/lab-listing/documents/${kind}`, form);
    },
    submit: () => fetchApi<LabSetupStatus>('/api/lab-listing/submit', { method: 'POST' }),
    /** Correct the registration details (before review, or after a rejection). */
    updateRegistration: (body: LabRegistrationUpdate) =>
      fetchApi<LabSetupStatus>('/api/lab-listing/registration', { method: 'PUT', body: JSON.stringify(body) }),
  },

  admin: {
    queue: (status: LabListingStatus, page: number, size = 20) =>
      fetchApi<LabQueuePage>(`/api/admin/lab-verifications?status=${status}&page=${page}&size=${size}`),
    /** A 5-minute link (S3), or the authenticated streaming path on local storage. Audited. */
    documentLink: (clinicId: string, kind: LabDocumentKind) =>
      fetchApi<{ url: string; expiresInSeconds: number; contentType: string }>(`${admin(clinicId)}/documents/${kind}`),
    /** Local storage: fetch the document with the session (a new tab wouldn't carry it). */
    documentFile: (path: string) => fetchGetFileApi(path),
    approve: (clinicId: string, note?: string) => fetchApi<void>(`${admin(clinicId)}/approve`, post({ note })),
    reject: (clinicId: string, note: string) => fetchApi<void>(`${admin(clinicId)}/reject`, post({ note })),
    revoke: (clinicId: string, note: string) => fetchApi<void>(`${admin(clinicId)}/revoke`, post({ note })),
  },
};
